import { Task } from "@common/types/task";
import { useCallback, useMemo } from "react";
import { useTaskBlockingSection } from "./useTaskBlockingSection";
import { TaskBlockingList } from "./TaskBlockingList";

interface BlockingSectionProps {
    task: Task;
    onBlockingChange?: (blockingTaskIds: string[]) => Promise<unknown>;
}

export const BlockingSection = ({
    task,
    onBlockingChange,
}: Readonly<BlockingSectionProps>) => {
    const { taskSummaries, goToTask, addOpen, setAddOpen } =
        useTaskBlockingSection(task);
    const blockingTaskIds = useMemo(
        () => task.blockingTaskIds ?? [],
        [task.blockingTaskIds],
    );
    const availableToAdd = useMemo(
        () =>
            taskSummaries.filter(
                (t) =>
                    t.id !== task.id &&
                    !blockingTaskIds.includes(t.id) &&
                    !(task.blockedByTaskIds ?? []).includes(t.id),
            ),
        [taskSummaries, task.id, blockingTaskIds, task.blockedByTaskIds],
    );

    const removeBlocking = useCallback(
        (id: string) => {
            onBlockingChange?.(
                blockingTaskIds.filter((taskId) => taskId !== id),
            );
        },
        [onBlockingChange, blockingTaskIds],
    );

    const addBlocking = useCallback(
        (taskId: string) => {
            onBlockingChange?.([...blockingTaskIds, taskId]);
        },
        [onBlockingChange, blockingTaskIds],
    );

    return (
        <TaskBlockingList
            taskIds={blockingTaskIds}
            availableToAdd={availableToAdd}
            onAdd={addBlocking}
            onRemove={removeBlocking}
            addOpen={addOpen}
            setAddOpen={setAddOpen}
            goToTask={goToTask}
            emptyLabel="None"
        />
    );
};
